const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Wires a `.modal` element: backdrop/Esc/[data-modal-dismiss] close it, focus
 * is trapped inside the dialog while open and restored to the opener on close,
 * and body scroll is locked. Returns { open, close }.
 */
export function createModal(root) {
  const dialog = root.querySelector('[data-modal-dialog]');
  let lastFocused = null;

  const trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const items = [...dialog.querySelectorAll(FOCUSABLE)].filter(
      (el) => !el.disabled && el.offsetParent !== null,
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') close();
    else trapFocus(event);
  };

  function open(returnFocusTo) {
    lastFocused = returnFocusTo instanceof HTMLElement ? returnFocusTo : document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown);
    dialog.focus();
  }

  function close() {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  }

  root.querySelectorAll('[data-modal-dismiss]').forEach((el) => {
    el.addEventListener('click', close);
  });

  return { open, close };
}

/**
 * Declarative triggers: any element with `data-modal-target="#id"` opens the
 * matching `.modal`. Modals opened programmatically don't need a trigger.
 */
export function initModals() {
  const instances = new Map();
  const get = (root) => {
    if (!instances.has(root)) instances.set(root, createModal(root));
    return instances.get(root);
  };

  document.querySelectorAll('[data-modal-target]').forEach((trigger) => {
    const root = document.querySelector(trigger.getAttribute('data-modal-target'));
    if (!root) return;
    trigger.addEventListener('click', () => get(root).open());
  });
}
